import { useState } from 'react';
import { Check, EyeOff } from 'lucide-react';
import { Button, Drawer, ErrorState, Skeleton } from '@/design/components';
import type { Json } from '@/lib/database.types';
import { SurveyQuestions } from '../SurveyQuestions';
import { isComplete, type SurveyAnswers, type SurveyQuestion } from '../api';
import { draftFromSurvey, type SurveyDraft } from './draft';
import { useSurveyForEdit } from './api';

/**
 * הסקר כפי שהעובד יראה אותו, לפני ששומרים אותו.
 *
 * מרנדר את SurveyQuestions — בדיוק אותו קוד שרץ במסך העובד, ולא
 * עותק שלו. תצוגה מקדימה שמחזיקה עותק מתיישנת בשקט, ואז היא
 * מראה משהו אחר ממה שיישלח.
 *
 * ברוחב טלפון, כי שם זה ייקרא: סקר שנראה סביר על מסך רחב יכול
 * להיות עשרים מסכי גלילה בכיס.
 *
 * הכפתורים פעילים בכוונה — כדי להרגיש את אורך הסקר ואת הקצב
 * צריך באמת לענות עליו. השליחה היא היחידה שאינה עושה דבר.
 */
export function SurveyPreview({ draft, onClose }: { draft: SurveyDraft; onClose: () => void }) {
  const [answers, setAnswers] = useState<SurveyAnswers>({});

  // הטיוטה עוד לא נשמרה ואין לשאלות מזהה. key של הטופס משמש
  // כמזהה, וזה בדיוק מה שהמזהה האמיתי יעשה אחר כך.
  const questions: SurveyQuestion[] = draft.questions.map((question, index) => ({
    id: question.key,
    prompt: question.prompt.trim() || `(שאלה ${index + 1} בלי נוסח)`,
    answer_kind: question.answer_kind,
    options:
      question.answer_kind === 'choice' || question.answer_kind === 'multi'
        ? question.options.map((option) => option.trim()).filter(Boolean)
        : [],
    sort_order: index + 1,
    section: question.section.trim() || null,
    is_required: question.is_required,
    allow_na: question.answer_kind === 'scale' && question.allow_na,
  }));

  // עדכון פונקציונלי ולא פרישה של answers: שתי תשובות שנרשמות
  // באותו tick נבנות שתיהן מאותו מצב ישן, והשנייה דורסת את
  // הראשונה בשקט
  const set = (questionId: string, value: Json) =>
    setAnswers((prev) => ({ ...prev, [questionId]: value }));

  const required = questions.filter((question) => question.is_required).length;
  const ready = isComplete(questions, answers);

  return (
    <Drawer
      title="תצוגה מקדימה"
      subtitle={
        questions.length === 1
          ? required === 1
            ? 'שאלה אחת, והיא חובה'
            : 'שאלה אחת, לא חובה'
          : `${questions.length} שאלות, ${required} מהן חובה`
      }
      onClose={onClose}
    >
      <div className="mx-auto grid w-full max-w-[26rem] content-start gap-3 rounded-md border border-line bg-raised p-3">
        <div className="grid gap-1">
          <h2 className="m-0 font-display text-display-m font-semibold">
            {draft.title.trim() || '(בלי שם)'}
          </h2>
          {draft.description.trim() && (
            <p className="m-0 text-body-sm text-muted">{draft.description}</p>
          )}
        </div>

        {draft.is_anonymous && (
          <p className="m-0 flex items-start gap-2 rounded-sm border border-line p-2 text-body-sm">
            <EyeOff size={18} strokeWidth={1.75} aria-hidden="true" className="mt-0.5 shrink-0" />
            <span>
              הסקר אנונימי. התשובה נשמרת בלי השם שלך, ונשמר רק הסניף. ההשתתפות נרשמת בנפרד, כדי לא
              לשלוח לך תזכורת.
            </span>
          </p>
        )}

        <SurveyQuestions questions={questions} answers={answers} onChange={set} />

        {/* מושבת תמיד: זו תצוגה, ואין מה לשלוח */}
        <Button variant="primary" disabled>
          <Check size={20} strokeWidth={1.75} aria-hidden="true" />
          שליחת התשובות
        </Button>

        <p className="m-0 text-label text-muted">
          {ready
            ? 'כל שאלות החובה נענו, וכאן הכפתור היה נדלק.'
            : 'הכפתור נדלק רק אחרי שכל שאלות החובה נענו.'}
        </p>
      </div>
    </Drawer>
  );
}

/**
 * תצוגה מקדימה לסקר שכבר נשמר, ישירות מהרשימה.
 *
 * בלי זה הדרך היחידה לראות סקר הייתה להיכנס לעריכה שלו — כלומר
 * לפתוח טופס שלם רק כדי להסתכל, ולסכן שמירה שלא התכוונת לה.
 * כאן רק קוראים.
 */
export function SurveyPreviewById({
  surveyId,
  onClose,
}: {
  surveyId: string;
  onClose: () => void;
}) {
  const survey = useSurveyForEdit(surveyId);

  if (survey.isPending) {
    return (
      <Drawer title="תצוגה מקדימה" onClose={onClose}>
        <Skeleton className="h-64" />
      </Drawer>
    );
  }
  if (!survey.data) {
    return (
      <Drawer title="תצוגה מקדימה" onClose={onClose}>
        <ErrorState onRetry={() => void survey.refetch()} />
      </Drawer>
    );
  }

  return <SurveyPreview draft={draftFromSurvey(survey.data)} onClose={onClose} />;
}
